/* 신뢰기기 쿠키(gs_td) 계정별 저장 — 서버 계약: sales-server docs/isms/endpoints.md
 *
 * 쿠키 값 = JSON 맵 { "<accountKey>": { v: "<deviceId>:<token>", t: <저장 epoch ms> }, ... }
 *  - accountKey = 로그인 이메일(소문자·trim)의 SHA-256 앞 16자. 이메일 원문은 쿠키에 두지 않는다.
 *  - 서버로는 로그인 중인 이메일의 v 하나만 clientMetadata.trustedDevice 로 실린다(계약 불변: 문자열 1개, TRUSTED_DEVICE_RE).
 *  - 항목 최대 MAX_ENTRIES, 4KB 상한 여유분(MAX_COOKIE_CHARS) 초과 시 t 가 오래된 항목부터 제거.
 *  - 구형 단일값("<uuid>:<token>", JSON 아님)은 읽히는 즉시 그 이메일의 항목으로 옮겨 새 형식으로 다시 쓴다.
 *  - 파싱 불가(JSON 도 구형도 아님)면 쿠키를 비우고 빈 맵 — 호출자는 trustedDevice 없이 OTP 로 진행한다. 여기서 throw 하지 않는다.
 *  - SHA-256 은 Web Crypto(crypto.subtle, 보안 컨텍스트 전용). 없으면 키를 못 만드니 읽기=null·쓰기=무시.
 */
import { getCookie, setCookie, deleteCookie } from "../helpers/Cookie";
import { TRUSTED_DEVICE_COOKIE } from "./constants";

export const TRUSTED_DEVICE_RE = /^[0-9a-fA-F-]{36}:[A-Za-z0-9_-]{16,200}$/;
export const MAX_ENTRIES = 10;
export const MAX_COOKIE_CHARS = 3800; // encodeURIComponent 후 길이 기준(이름·속성 포함 4096 미만 보장)
const KEY_HEX_LEN = 16;
const DEFAULT_DAYS = 30;

const subtle = () => (typeof crypto !== "undefined" && crypto.subtle ? crypto.subtle : null);

export const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

/** 이메일 → 쿠키 맵 키(SHA-256 hex 앞 16자). 이메일이 비었거나 Web Crypto 가 없으면 null. */
export const accountKey = async (email) => {
  const norm = normalizeEmail(email);
  const s = subtle();
  if (!norm || !s || typeof TextEncoder === "undefined") return null;
  try {
    const digest = await s.digest("SHA-256", new TextEncoder().encode(norm));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, KEY_HEX_LEN);
  } catch (e) {
    return null;
  }
};

const isEntry = (e) => !!(e && typeof e === "object" && typeof e.v === "string" && TRUSTED_DEVICE_RE.test(e.v));

/** 쿠키 원문 → { entries, legacy }. entries 는 형식이 맞는 항목만, legacy 는 구형 단일값(마이그레이션 대상). invalid 는 비워야 할 쿠키. */
export const parseTrustedDevices = (raw) => {
  if (!raw) return { entries: {}, legacy: null, invalid: false };
  if (TRUSTED_DEVICE_RE.test(raw)) return { entries: {}, legacy: raw, invalid: false };
  let obj;
  try {
    obj = JSON.parse(raw);
  } catch (e) {
    return { entries: {}, legacy: null, invalid: true };
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return { entries: {}, legacy: null, invalid: true };
  const entries = {};
  Object.keys(obj).forEach((k) => {
    if (isEntry(obj[k])) entries[k] = { v: obj[k].v, t: Number(obj[k].t) || 0 };
  });
  return { entries, legacy: null, invalid: false };
};

export const serializeTrustedDevices = (entries) => JSON.stringify(entries);

/** 개수·길이 상한에 맞춰 오래된 항목(t 오름차순)부터 제거한 새 맵 */
export const pruneTrustedDevices = (entries, { max = MAX_ENTRIES, maxChars = MAX_COOKIE_CHARS } = {}) => {
  const keys = Object.keys(entries).sort((a, b) => entries[b].t - entries[a].t); // 최신 우선
  let kept = keys.slice(0, max);
  const build = () =>
    kept.reduce((acc, k) => {
      acc[k] = entries[k];
      return acc;
    }, {});
  let out = build();
  while (kept.length > 0 && encodeURIComponent(serializeTrustedDevices(out)).length > maxChars) {
    kept = kept.slice(0, -1);
    out = build();
  }
  return out;
};

const readMap = () => {
  let raw;
  try {
    raw = getCookie(TRUSTED_DEVICE_COOKIE); // 변조된 %-시퀀스면 decodeURIComponent 가 URIError
  } catch (e) {
    raw = "\u0000invalid"; // JSON 도 구형도 아닌 값 → 아래에서 invalid 처리
  }
  const parsed = parseTrustedDevices(raw);
  if (parsed.invalid) deleteCookie(TRUSTED_DEVICE_COOKIE);
  return parsed;
};

const writeMap = (entries, days) => {
  const pruned = pruneTrustedDevices(entries);
  if (!Object.keys(pruned).length) {
    deleteCookie(TRUSTED_DEVICE_COOKIE);
    return;
  }
  const expires = new Date(Date.now() + (days || DEFAULT_DAYS) * 86400 * 1000);
  // Cookie 헬퍼는 false 도 "secure=false" 로 붙여 Secure 속성이 되므로 https 일 때만 키를 넣는다
  const secure = window.location.protocol === "https:" ? { secure: true } : {};
  setCookie(TRUSTED_DEVICE_COOKIE, serializeTrustedDevices(pruned), { expires, ...secure, samesite: "Lax" });
};

/** 로그인 이메일의 "<deviceId>:<token>" 또는 null. 구형 쿠키는 이 이메일의 항목으로 옮기고 새 형식으로 다시 쓴다. */
export const getTrustedDevice = async (email) => {
  const key = await accountKey(email);
  if (!key) return null;
  const { entries, legacy } = readMap();
  if (legacy) {
    entries[key] = { v: legacy, t: Date.now() };
    writeMap(entries);
    return legacy;
  }
  const e = entries[key];
  return e ? e.v : null;
};

/** 해당 이메일 항목만 갱신(다른 계정 항목 유지). 쿠키 만료는 days 로 다시 민다. */
export const setTrustedDevice = async (email, value, days) => {
  const key = await accountKey(email);
  if (!key || !TRUSTED_DEVICE_RE.test(value || "")) return false;
  const { entries } = readMap(); // 구형 단일값은 소유 계정을 알 수 없으므로 지금 계정의 새 토큰이 대체한다
  entries[key] = { v: value, t: Date.now() };
  writeMap(entries, days);
  return true;
};

/** 해당 이메일 항목만 삭제. 마지막 항목이면 쿠키 자체를 지운다. */
export const removeTrustedDevice = async (email) => {
  const key = await accountKey(email);
  if (!key) return false;
  const { entries, legacy } = readMap();
  if (legacy) {
    deleteCookie(TRUSTED_DEVICE_COOKIE); // 구형 단일값은 계정 구분이 없으므로 통째로
    return true;
  }
  if (!entries[key]) return false;
  delete entries[key];
  writeMap(entries);
  return true;
};
